import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Users,
  ShoppingCart,
  DollarSign,
  RefreshCw,
  Wallet,
  Clock,
  Activity,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import api from '../utils/api';

function StatCard({ icon: Icon, label, value, subValue, color = 'blue', loading }) {
  const colors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
    cyan: 'text-cyan-400',
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-xl border border-gray-700/30">
      <Icon size={16} className={colors[color]} />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        {loading ? (
          <div className="h-5 bg-gray-700 rounded animate-pulse mt-0.5 w-16" />
        ) : (
          <p className="text-lg font-bold text-white leading-tight">{value ?? '—'}</p>
        )}
      </div>
      {!loading && subValue && (
        <p className="text-[10px] text-gray-600 text-right max-w-[80px] leading-tight">{subValue}</p>
      )}
    </div>
  );
}

function StatGroup({ title, children }) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{title}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {children}
      </div>
    </div>
  );
}

function MiniChart({ data, label, color = 'blue' }) {
  const [hovered, setHovered] = useState(null);

  if (!data || data.length === 0) return null;

  const max = Math.max(...data.map(d => d.value), 1);
  const min = Math.min(...data.map(d => d.value), 0);
  const total = data.reduce((sum, d) => sum + d.value, 0);

  const strokeColors = {
    blue: '#3b82f6',
    green: '#22c55e',
    purple: '#a855f7',
    amber: '#f59e0b',
    rose: '#f43f5e',
    cyan: '#06b6d4',
  };

  const stroke = strokeColors[color] || strokeColors.blue;

  // Layout with axes
  const padLeft = 45;
  const padRight = 10;
  const padTop = 10;
  const padBottom = 28;
  const totalW = 320;
  const totalH = 140;
  const chartW = totalW - padLeft - padRight;
  const chartH = totalH - padTop - padBottom;

  // Y-axis ticks (4 levels)
  const yRange = max - min || 1;
  const yTicks = [0, 1, 2, 3].map(i => {
    const val = max - (i / 3) * yRange;
    return {
      value: val,
      y: padTop + (i / 3) * chartH,
      label: val >= 1000 ? `${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k` : Math.round(val).toString(),
    };
  });

  const points = data.map((d, i) => {
    const x = padLeft + (data.length === 1 ? chartW / 2 : (i / (data.length - 1)) * chartW);
    const y = max === min
      ? padTop + chartH / 2
      : padTop + ((max - d.value) / yRange) * chartH;
    return { x, y, value: d.value, label: d.label, index: i };
  });

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaBottom = padTop + chartH;
  const area = `${line} L${points[points.length - 1].x},${areaBottom} L${points[0].x},${areaBottom} Z`;

  // X-axis: show every Nth label so they don't overlap
  const maxLabels = 7;
  const step = Math.max(1, Math.ceil(data.length / maxLabels));

  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * totalW;
    let closest = 0;
    let closestDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    setHovered(closest);
  };

  const handleTouchMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const touch = e.touches[0];
    const mouseX = ((touch.clientX - rect.left) / rect.width) * totalW;
    let closest = 0;
    let closestDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    setHovered(closest);
  };

  return (
    <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="text-xs text-gray-500">
          {hovered !== null
            ? `${points[hovered].label}: ${Math.round(points[hovered].value).toLocaleString('ru-RU')}`
            : `Σ ${Math.round(total).toLocaleString('ru-RU')}`
          }
        </p>
      </div>
      <svg
        viewBox={`0 0 ${totalW} ${totalH}`}
        className="w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setHovered(null)}
        style={{ touchAction: 'none' }}
      >
        <defs>
          <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.3" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines & labels */}
        {yTicks.map((tick, i) => (
          <g key={`yt${i}`}>
            <line x1={padLeft} y1={tick.y} x2={totalW - padRight} y2={tick.y} stroke="#374151" strokeWidth="0.5" strokeDasharray="4,3" />
            <text x={padLeft - 6} y={tick.y + 3.5} textAnchor="end" fill="#6b7280" fontSize="9" fontFamily="monospace">
              {tick.label}
            </text>
          </g>
        ))}

        {/* Area fill */}
        <path d={area} fill={`url(#grad-${color})`} />
        {/* Line */}
        <path d={line} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={hovered === i ? 5 : 3}
            fill={hovered === i ? '#fff' : stroke}
            stroke={hovered === i ? stroke : 'none'}
            strokeWidth={hovered === i ? 2 : 0}
          />
        ))}

        {/* X-axis date labels */}
        {points.map((p, i) => {
          if (i % step !== 0 && i !== points.length - 1) return null;
          return (
            <text key={`xl${i}`} x={p.x} y={totalH - 4} textAnchor="middle" fill="#6b7280" fontSize="9">
              {p.label}
            </text>
          );
        })}

        {/* Hover vertical line & tooltip */}
        {hovered !== null && (() => {
          const hp = points[hovered];
          const tooltipBelow = hp.y < padTop + 26;
          const tooltipY = tooltipBelow ? hp.y + 10 : hp.y - 22;
          const textY = tooltipBelow ? hp.y + 21 : hp.y - 11;
          const valStr = Math.round(hp.value).toLocaleString('ru-RU');
          const boxW = Math.max(56, valStr.length * 7 + 12);
          return (
            <>
              <line x1={hp.x} y1={padTop} x2={hp.x} y2={areaBottom} stroke={stroke} strokeWidth="1" strokeOpacity="0.4" strokeDasharray="3,3" />
              <rect
                x={Math.min(Math.max(hp.x - boxW / 2, 0), totalW - boxW)}
                y={tooltipY}
                width={boxW}
                height={16}
                rx={4}
                fill="#1f2937"
                stroke={stroke}
                strokeWidth="1"
                strokeOpacity="0.6"
              />
              <text
                x={hp.x}
                y={textY}
                textAnchor="middle"
                fill="#fff"
                fontSize="9"
                fontWeight="bold"
              >
                {valStr}
              </text>
            </>
          );
        })()}
      </svg>
    </div>
  );
}

export default function DashboardScreen() {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [rateSettings, setRateSettings] = useState(null);
  const [altynBalance, setAltynBalance] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('day');
  const [chartDays, setChartDays] = useState(7);
  const [dailyOpen, setDailyOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDashboardData(period, chartDays);

      if (data.stats) setStats(data.stats);
      if (data.daily) setDailyStats(data.daily);
      if (data.realtime) setRealtime(data.realtime);
      if (data.dashboard) setDashboard(data.dashboard);
      if (data.rates) setRateSettings(data.rates.settings || data.rates);
      if (data.balance) setAltynBalance(data.balance);
      if (data.orders) setOrders(data.orders);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, chartDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh realtime every 30 seconds
  useEffect(() => {
    let active = true;
    const interval = setInterval(async () => {
      try {
        const data = await api.getDashboardData(period, chartDays);
        if (!active) return;
        if (data.realtime) setRealtime(data.realtime);
        if (data.stats) setStats(data.stats);
      } catch { /* ignore */ }
    }, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [period, chartDays]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    if (typeof num !== 'number') num = Number(num);
    if (isNaN(num)) return '—';
    return Math.round(num).toLocaleString('ru-RU');
  };

  const formatCurrency = (num, currency = '') => {
    if (num === null || num === undefined) return '—';
    return `${formatNumber(num)} ${currency}`.trim();
  };

  // Computed metrics
  const retentionRate = (() => {
    if (!orders || orders.length === 0) return null;
    const completed = orders.filter(o => o.status === 'completed');
    const byUser = {};
    completed.forEach(o => { if (o.userId) byUser[o.userId] = (byUser[o.userId] || 0) + 1; });
    const total = Object.keys(byUser).length;
    if (total === 0) return null;
    const repeat = Object.values(byUser).filter(c => c > 1).length;
    return ((repeat / total) * 100).toFixed(1);
  })();

  const avgProcessingTime = (() => {
    if (!orders || orders.length === 0) return null;
    const MAX_MS = 20 * 60000; // 20 минут
    const valid = orders.filter(o => {
      if (!o.paidAt || !o.completedAt) return false;
      return (new Date(o.completedAt) - new Date(o.paidAt)) <= MAX_MS;
    });
    if (valid.length === 0) return null;
    const totalMs = valid.reduce((sum, o) => sum + (new Date(o.completedAt) - new Date(o.paidAt)), 0);
    const avgMin = totalMs / valid.length / 60000;
    return `${Math.round(avgMin)} мин`;
  })();

  const kycRejectionRate = (() => {
    const v = stats?.kyc?.verified || 0;
    const r = stats?.kyc?.rejected || 0;
    if (v + r === 0) return null;
    return ((r / (v + r)) * 100).toFixed(1);
  })();


  // Prepare chart data from daily stats
  const days = Array.isArray(dailyStats) ? dailyStats : (dailyStats?.days || dailyStats?.daily || []);

  const ordersChartData = days.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.orders || d.count || 0,
  }));

  const volumeRubChartData = days.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.rub || d.volumeRUB || 0,
  }));

  const volumeCnyChartData = days.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.cny || d.volumeCNY || 0,
  }));

  const newUsersChartData = days.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.newUsers || d.new_users || 0,
  }));

  // KYC data from dashboard.period.dailyStats
  const dashboardDays = dashboard?.period?.dailyStats || [];
  const kycChartData = dashboardDays.map(d => ({
    label: d.date?.slice(5) || '',
    value: (d.kyc?.completed || 0) + (d.kyc?.started || 0),
  }));

  const kycVerifiedChartData = days.map(d => ({
    label: d.date?.slice(5) || '',
    value: d.kycVerified ?? d.kyc_verified ?? d.kyc?.verified ?? 0,
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Дашборд</h2>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
            {['day', 'week', 'month', 'all'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  period === p
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {{ day: 'День', week: 'Неделя', month: 'Месяц', all: 'Всё' }[p]}
              </button>
            ))}
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="p-2 bg-gray-800/50 rounded-xl border border-gray-700/50 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Realtime banner */}
      {realtime && (
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-xl p-4 mb-6 border border-blue-800/30">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium">Реалтайм</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-400">Активные за 5 мин</p>
              <p className="text-lg font-bold text-white">{realtime.activeUsers ?? realtime.active5min ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Запросов/час</p>
              <p className="text-lg font-bold text-white">{realtime.requestsPerHour ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Заблок. IP</p>
              <p className="text-lg font-bold text-white">{realtime.blockedIPs ?? realtime.blockedIps ?? '—'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats groups */}
      <StatGroup title="Ордера и объём">
        <StatCard icon={ShoppingCart} label="Ордера" value={formatNumber(stats?.orders?.total ?? stats?.orders)} subValue={stats?.orders?.completed ? `Завершено: ${stats.orders.completed}` : undefined} color="blue" loading={loading} />
        <StatCard icon={DollarSign} label="Объём CNY" value={formatCurrency(stats?.volume?.totalCNY ?? stats?.volumeCNY, '¥')} color="green" loading={loading} />
        <StatCard icon={TrendingUp} label="Объём RUB" value={formatCurrency(stats?.volume?.totalRUB ?? stats?.volumeRUB, '₽')} color="purple" loading={loading} />
        <StatCard icon={Clock} label="Ср. обработка" value={avgProcessingTime ?? '—'} subValue="paid → completed" color="amber" loading={loading} />
      </StatGroup>

      <StatGroup title="Пользователи и KYC">
        <StatCard icon={Users} label="Новые юзеры" value={formatNumber(stats?.users?.newFromStart ?? stats?.users)} subValue={stats?.users?.appOpens ? `Открытий: ${stats.users.appOpens}` : undefined} color="cyan" loading={loading} />
        <StatCard icon={Users} label="Retention" value={retentionRate != null ? `${retentionRate}%` : '—'} subValue="> 1 успешной заявки" color="green" loading={loading} />
        <StatCard icon={Clock} label="KYC верифицировано" value={formatNumber(stats?.kyc?.verified ?? stats?.kycVerified)} color="rose" loading={loading} />
        <StatCard icon={TrendingUp} label="Конверсия KYC" value={stats?.kyc?.conversionRate != null ? `${stats.kyc.conversionRate}%` : '—'} color="rose" loading={loading} />
        <StatCard icon={Activity} label="Отказы KYC" value={kycRejectionRate != null ? `${kycRejectionRate}%` : '—'} subValue={`${stats?.kyc?.rejected || 0} из ${(stats?.kyc?.verified || 0) + (stats?.kyc?.rejected || 0)}`} color="rose" loading={loading} />
      </StatGroup>

      <StatGroup title="Финансы">
        <StatCard icon={DollarSign} label="Прибыль" value={stats?.profit?.estimatedUSDT != null ? `${stats.profit.estimatedUSDT.toFixed(2)} USDT` : '—'} subValue={stats?.profit?.marginPercent ? `Маржа: ${stats.profit.marginPercent}%` : undefined} color="amber" loading={loading} />
        <StatCard icon={Wallet} label="Баланс Altyn" value={formatCurrency(stats?.altynBalance?.balance ?? altynBalance?.balance, stats?.altynBalance?.currency || 'USDT')} color="green" loading={loading} />
        <StatCard icon={TrendingUp} label="Курс RUB/CNY" value={rateSettings?.liveRate ?? rateSettings?.rate ?? '—'} subValue={rateSettings?.marginPercent ? `Маржа: ${rateSettings.marginPercent}%` : undefined} color="blue" loading={loading} />
      </StatGroup>

      {/* Charts */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-white">Графики</h3>
        <div className="flex bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
          {[
            { value: 7, label: '7д' },
            { value: 14, label: '14д' },
            { value: 30, label: '30д' },
            { value: 90, label: '90д' },
          ].map(p => (
            <button
              key={p.value}
              onClick={() => setChartDays(p.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                chartDays === p.value
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <MiniChart data={ordersChartData} label="Ордера по дням" color="blue" />
        <MiniChart data={volumeRubChartData} label="Объём RUB по дням" color="purple" />
        <MiniChart data={volumeCnyChartData} label="Объём CNY по дням" color="green" />
        <MiniChart data={newUsersChartData} label="Новые пользователи" color="cyan" />
        {kycVerifiedChartData.length > 0 && kycVerifiedChartData.some(d => d.value > 0) && (
          <MiniChart data={kycVerifiedChartData} label="KYC верификации по дням" color="rose" />
        )}
        {kycChartData.length > 0 && kycChartData.some(d => d.value > 0) && (
          <MiniChart data={kycChartData} label="KYC активность (30 дней)" color="purple" />
        )}

        {/* Top events from dashboard */}
        {dashboard?.topEvents && (
          <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
            <p className="text-xs text-gray-400 font-medium mb-3">Top события (30 дней)</p>
            <div className="space-y-2">
              {dashboard.topEvents.slice(0, 6).map((event, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300 truncate">{event.event || event.type || event.name}</span>
                  <span className="text-sm font-medium text-white">{formatNumber(event.count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Daily breakdown */}
      {days.length > 0 && (
        <div>
          <button
            onClick={() => setDailyOpen(o => !o)}
            className="flex items-center gap-2 mb-3 group"
          >
            <h3 className="text-sm font-medium text-white group-hover:text-purple-400 transition-colors">
              Разбивка по дням ({days.length})
            </h3>
            {dailyOpen
              ? <ChevronUp size={14} className="text-gray-400" />
              : <ChevronDown size={14} className="text-gray-400" />
            }
          </button>
          {dailyOpen && (
            <div className="space-y-2">
              {days.map((day, i) => (
                <div key={i} className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white font-medium">{day.date}</span>
                    <span className="text-xs text-gray-400">+{day.newUsers ?? 0} юзеров</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500">Ордера</p>
                      <p className="text-white font-medium">{formatNumber(day.orders ?? day.count)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">CNY</p>
                      <p className="text-white font-medium">{formatCurrency(day.cny ?? day.volumeCNY)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">RUB</p>
                      <p className="text-white font-medium">{formatCurrency(day.rub ?? day.volumeRUB)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
